import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import { 
  Sparkles, ArrowLeft, Upload, CheckCircle2, 
  Trash2, Globe, Eye, Code, Check, AlertCircle, FileText
} from 'lucide-react';
import { API_BASE } from '@/shared/services/api';

const API_URL = API_BASE;

export const SkinManager = () => {
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ezerpToken');
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchVariants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/landing/variants`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setVariants(data);
      } else {
        toast.error('Failed to load theme variants');
      }
    } catch (err) {
      toast.error('Error fetching variants: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVariants(); }, [fetchVariants]);

  const handleActivate = async (slug) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/variants/${slug}/activate`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        toast.success('Theme variant activated successfully!');
        fetchVariants();
      } else {
        const errData = await response.json();
        toast.error(errData.detail || 'Failed to activate variant');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const handleToggleDemoCredentials = async (slug, currentValue) => {
    try {
      const response = await fetch(`${API_URL}/api/landing/variants/${slug}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ show_demo_credentials: !currentValue }),
      });
      if (response.ok) {
        toast.success('Settings updated successfully!');
        setVariants(prev => prev.map(v =>
          v.slug === slug ? { ...v, show_demo_credentials: !currentValue } : v
        ));
      } else {
        toast.error('Failed to update variant settings');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this custom skin?')) return;
    try {
      const response = await fetch(`${API_URL}/api/landing/variants/${slug}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        toast.success('Custom skin deleted successfully!');
        fetchVariants();
      } else {
        const errData = await response.json();
        toast.error(errData.detail || 'Failed to delete variant');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      toast.error('Please upload a valid ZIP package.');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const response = await fetch(`${API_URL}/api/landing/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (response.ok) {
        toast.success('Theme ZIP imported successfully!');
        if (fileInputRef.current) fileInputRef.current.value = '';
        fetchVariants();
      } else {
        const errData = await response.json();
        toast.error(errData.detail || 'Failed to import ZIP package');
      }
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const activeVariant = variants.find(v => v.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost" size="icon" asChild
            className="hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
          >
            <a href="/admin/landing">
              <ArrowLeft className="w-5 h-5" />
            </a>
          </Button>
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white flex items-center gap-2 tracking-tight">
              <Sparkles className="w-8 h-8 text-pink-500 animate-pulse" />
              Skins &amp; Temas
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Personalize a aparência visual de sua landing page utilizando skins e temas dinâmicos.
            </p>
          </div>
        </div>

        {/* Import ZIP */}
        <div>
          <input
            type="file" ref={fileInputRef} onChange={handleFileUpload}
            accept=".zip" className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="gap-2 bg-gradient-to-r from-pink-600 to-indigo-600 hover:from-pink-700 hover:to-indigo-700 text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
          >
            <Upload className="w-4 h-4" />
            {uploading ? 'Importando ZIP...' : 'Ingerir ZIP'}
          </Button>
        </div>
      </div>

      {/* ── Active Theme Card ── */}
      {activeVariant && (
        <Card className="relative overflow-hidden shadow-lg border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900/60 backdrop-blur-xl">
          {/* Decorative glows */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />

          <CardHeader className="border-b border-slate-200 dark:border-slate-800/80 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <Badge className="bg-pink-600 text-white font-semibold mb-2">Ativo</Badge>
                <CardTitle className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  {activeVariant.name}
                </CardTitle>
                <CardDescription className="text-slate-500 dark:text-slate-400">
                  {activeVariant.description}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700">
                  ID: {activeVariant.theme_id}
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 capitalize">
                  {activeVariant.source_type}
                </span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

              {/* Color Palette */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Paleta de Cores</h4>
                <div className="flex flex-wrap gap-3">
                  {activeVariant.manifest?.theme?.colors &&
                    Object.entries(activeVariant.manifest.theme.colors).map(([key, val]) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/60 rounded-lg p-2 pr-3"
                      >
                        <div
                          className="w-6 h-6 rounded-md shadow-inner border border-slate-300 dark:border-slate-600/50"
                          style={{ backgroundColor: val }}
                        />
                        <div className="text-left">
                          <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 capitalize">
                            {key.replace(/([A-Z])/g, ' $1')}
                          </p>
                          <p className="text-[10px] font-mono text-slate-400 dark:text-slate-500">{val}</p>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>

              {/* Demo Credentials Toggle */}
              <div className="flex flex-col justify-center bg-slate-50 dark:bg-slate-800/20 border border-slate-200 dark:border-slate-700/40 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-slate-900 dark:text-white text-base font-semibold">
                      Exibir Credenciais de Demonstração
                    </Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Exibe um painel flutuante com credenciais de teste para facilitar o acesso de parceiros e revisores.
                    </p>
                  </div>
                  <Switch
                    checked={activeVariant.show_demo_credentials}
                    onCheckedChange={() => handleToggleDemoCredentials(activeVariant.slug, activeVariant.show_demo_credentials)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Theme Grid ── */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
          <Globe className="w-5 h-5 text-indigo-500 dark:text-indigo-400" />
          Temas Disponíveis
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {variants.map((theme) => {
            const colors = theme.manifest?.theme?.colors || {};
            const bgImage = theme.preview_asset || (theme.manifest?.theme?.assets?.background);

            return (
              <Card
                key={theme.id}
                className={`relative overflow-hidden transition-all duration-300
                  bg-white dark:bg-slate-900/40
                  border-slate-200 dark:border-slate-700/60
                  hover:border-slate-300 dark:hover:border-slate-500/80
                  hover:shadow-lg group
                  ${theme.is_active ? 'ring-2 ring-pink-500/50 border-pink-400 dark:border-pink-500/80' : ''}
                `}
              >
                {/* Thumbnail */}
                <div className="h-44 relative bg-slate-100 dark:bg-slate-950 overflow-hidden border-b border-slate-200 dark:border-slate-800">
                  {bgImage ? (
                    <img
                      src={`${API_URL}${bgImage}`.replace(/\/+/g, '/').replace(':/', '://')}
                      alt={theme.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-100 to-indigo-100 dark:from-slate-900 dark:to-indigo-950 flex items-center justify-center">
                      <Code className="w-12 h-12 text-slate-300 dark:text-slate-700" />
                    </div>
                  )}

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 dark:from-slate-950 to-transparent" />

                  {/* Tags */}
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    {theme.is_active && (
                      <span className="bg-pink-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                        Ativo
                      </span>
                    )}
                    {theme.is_builtin ? (
                      <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                        Built-in
                      </span>
                    ) : (
                      <span className="bg-purple-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                        Importado
                      </span>
                    )}
                  </div>

                  {/* Color strip */}
                  <div className="absolute bottom-3 right-3 flex gap-1 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md p-1.5 rounded-md border border-slate-200 dark:border-slate-800">
                    {Object.values(colors).slice(0, 4).map((c, idx) => (
                      <div
                        key={idx}
                        className="w-3.5 h-3.5 rounded-sm border border-slate-300 dark:border-slate-700"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>

                {/* Card body */}
                <CardContent className="pt-4 space-y-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors">
                      {theme.name}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2 h-8">
                      {theme.description || 'Nenhuma descrição fornecida.'}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-200 dark:border-slate-800/80">
                    <div className="flex gap-2">
                      {!theme.is_active ? (
                        <Button
                          onClick={() => handleActivate(theme.slug)}
                          size="sm"
                          className="bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-slate-200"
                        >
                          Usar
                        </Button>
                      ) : (
                        <Button
                          disabled size="sm"
                          className="bg-pink-500/20 text-pink-600 dark:text-pink-400 border border-pink-400/40 dark:border-pink-500/30 font-semibold"
                        >
                          <Check className="w-3.5 h-3.5 mr-1" /> Ativo
                        </Button>
                      )}

                      <Button
                        variant="outline" size="sm"
                        className="bg-transparent border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                        asChild
                      >
                        <a href="/landing-preview" target="_blank">
                          <Eye className="w-3.5 h-3.5" />
                        </a>
                      </Button>
                    </div>

                    {!theme.is_builtin && (
                      <Button
                        onClick={() => handleDelete(theme.slug)}
                        variant="ghost" size="icon"
                        className="text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 h-8 w-8"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Helper badge component
const Badge = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors ${className}`}>
    {children}
  </span>
);

export default SkinManager;
